/**
 * High-level operations for the hitomi source.
 *
 * Each function here composes the lower-level modules (gg / nozomi / btree /
 * parsers) into something `index.ts` can hand back as a `ComicSource` method.
 *
 * Pagination model: we treat hitomi as a flat ordered ID list. `searchByTag`
 * and `listPopular` slice that list with HTTP Range; `searchByQuery` collects
 * the B-tree intersection then paginates locally. `getGallery` is a single
 * `/galleries/<id>.js` fetch.
 *
 * Page size is fixed to 25 unless callers override — that matches the
 * upstream's default and keeps each nozomi Range request to ~100 bytes.
 */

import { NetworkError, ParseError } from '@/lib/errors';
import type { ComicCard, ComicDetails, Page, PageRef } from '../types';
import { searchTitleTerms } from './btree';
import { getImageContext, computeImageUrl, RESOURCE_DOMAIN } from './gg';
import { fetchNozomi, popularRangeOrderBy } from './nozomi';
import {
  HITOMI_SOURCE_ID,
  mapToComicCard,
  mapToComicDetails,
  parseGalleryInfoJsonp,
  toHitomiImages,
} from './parsers';
import type { HitomiExtension, HitomiImage, PopularRange, RawGalleryInfo } from './types';

/** Default page size for hitomi list-style endpoints. */
export const DEFAULT_PAGE_SIZE = 25;

const inflightRawGalleries = new Map<string, Promise<RawGalleryInfo>>();

interface ListOpts {
  page?: number;
  pageSize?: number;
  language?: string;
  orderBy?: string;
  signal?: AbortSignal;
}

/**
 * Tag-based browse — hits the appropriate `.nozomi` file for the given tag
 * and language, then resolves each ID to a `ComicCard` via {@link getGallery}.
 *
 * Tag must already be in `{namespace, name}` form. Use {@link parseTagSpec}
 * to convert from `"male:big_breasts"`-style strings.
 */
export async function searchByTag(
  tag: { namespace: string; name: string },
  opts: ListOpts = {},
): Promise<Page<ComicCard>> {
  const page = opts.page ?? 0;
  const pageSize = opts.pageSize ?? DEFAULT_PAGE_SIZE;
  const nozomi = await fetchNozomi({
    tag,
    language: opts.language ?? 'all',
    ...(opts.orderBy !== undefined ? { orderBy: opts.orderBy } : {}),
    page,
    pageSize,
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  return materializeIdPage(nozomi.ids, page, pageSize, nozomi.hasMore, nozomi.totalItems, opts.signal);
}

/**
 * Popular browse — uses the `popular/<range>` nozomi file.
 */
export async function listPopular(opts: {
  timeframe?: PopularRange;
  page?: number;
  pageSize?: number;
  language?: string;
  signal?: AbortSignal;
} = {}): Promise<Page<ComicCard>> {
  const page = opts.page ?? 0;
  const pageSize = opts.pageSize ?? DEFAULT_PAGE_SIZE;
  const timeframe: PopularRange = opts.timeframe ?? 'week';
  const nozomi = await fetchNozomi({
    orderBy: popularRangeOrderBy(timeframe),
    language: opts.language ?? 'all',
    page,
    pageSize,
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  return materializeIdPage(nozomi.ids, page, pageSize, nozomi.hasMore, nozomi.totalItems, opts.signal);
}

/**
 * Date-added browse — uses hitomi's default `index-<lang>.nozomi` file.
 */
export async function listLatest(opts: {
  page?: number;
  pageSize?: number;
  language?: string;
  signal?: AbortSignal;
} = {}): Promise<Page<ComicCard>> {
  const page = opts.page ?? 0;
  const pageSize = opts.pageSize ?? DEFAULT_PAGE_SIZE;
  const nozomi = await fetchNozomi({
    language: opts.language ?? 'all',
    page,
    pageSize,
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  return materializeIdPage(nozomi.ids, page, pageSize, nozomi.hasMore, nozomi.totalItems, opts.signal);
}

/**
 * Free-form keyword search — uses the B-tree title index. Intersection of
 * all whitespace-separated terms (no OR support; matches upstream).
 *
 * Note: B-tree search returns the FULL intersection up-front; we paginate
 * locally. For very common terms this may still be hundreds of IDs and a
 * few hundred concurrent gallery fetches if the user pages deep. The
 * `getGallery` batching in {@link materializeIdPage} is sequential — we
 * trade latency for politeness toward hitomi.la's static origin.
 */
export async function searchByQuery(
  query: string,
  opts: ListOpts = {},
): Promise<Page<ComicCard>> {
  const page = opts.page ?? 0;
  const pageSize = opts.pageSize ?? DEFAULT_PAGE_SIZE;
  const allIds = await searchTitleTerms(query);

  const totalItems = allIds.length;
  const start = page * pageSize;
  const end = start + pageSize;
  const ids = allIds.slice(start, end);
  const hasMore = end < totalItems;

  const items = await Promise.all(ids.map((id) => safeGalleryCard(String(id), opts.signal)));
  return {
    items: items.filter((x): x is ComicCard => x !== undefined),
    page,
    hasMore,
    totalItems,
  };
}

/**
 * Detail fetch: `/galleries/<id>.js` → `RawGalleryInfo` → `ComicDetails`.
 */
export async function getGallery(id: string, opts: { signal?: AbortSignal } = {}): Promise<ComicDetails> {
  const raw = await fetchRawGallery(id, opts.signal);
  return mapToComicDetails(raw);
}

/**
 * Lower-level fetch used by both `getGallery` and `materializeIdPage`. Useful
 * to expose for testing the network path; production code should generally
 * call `getGallery` or `getImageUrls`.
 */
export async function fetchRawGallery(id: string, signal?: AbortSignal): Promise<RawGalleryInfo> {
  const cleanId = id.trim();
  if (!cleanId || !/^\d+$/.test(cleanId)) {
    throw new ParseError(`hitomi: invalid gallery id "${id}"`, { source: 'hitomi' });
  }
  if (!signal) {
    const existing = inflightRawGalleries.get(cleanId);
    if (existing) return existing;

    const promise = fetchRawGalleryUncached(cleanId).finally(() => {
      inflightRawGalleries.delete(cleanId);
    });
    inflightRawGalleries.set(cleanId, promise);
    return promise;
  }
  return fetchRawGalleryUncached(cleanId, signal);
}

async function fetchRawGalleryUncached(
  cleanId: string,
  signal?: AbortSignal,
): Promise<RawGalleryInfo> {
  const url = `https://${RESOURCE_DOMAIN}/galleries/${cleanId}.js`;
  const { proxyFetch } = await import('@/lib/proxy');
  const init: Parameters<typeof proxyFetch>[1] = { debugLabel: `hitomi.gallery.${cleanId}` };
  if (signal) init.signal = signal;
  const res = await proxyFetch(url, init);
  if (res.status === 404) {
    throw new ParseError(`hitomi: gallery ${cleanId} not found (404)`, { source: 'hitomi' });
  }
  if (!res.ok) {
    throw new NetworkError(`hitomi: gallery fetch ${res.status}`, {
      source: 'hitomi',
      status: res.status,
    });
  }
  const text = await res.text();
  return parseGalleryInfoJsonp(text);
}

/**
 * Build the list of full-size `<img src>` URLs for every page in a gallery.
 *
 * This is the entry point for the reader: callers pass the gallery info (so
 * the reader can hold onto it for next/prev) and an optional page index. If
 * `pageIdx` is omitted, all URLs are returned.
 */
export async function getImageUrls(
  raw: RawGalleryInfo,
  opts: { pageIdx?: number; extension?: HitomiExtension } = {},
): Promise<string[]> {
  const images = toHitomiImages(raw);
  const ctx = await getImageContext();
  if (opts.pageIdx !== undefined) {
    const image = images[opts.pageIdx];
    if (!image) return [];
    return [computeImageUrl(image, ctx, { extension: opts.extension ?? 'avif' })];
  }
  return images.map((image) => computeImageUrl(image, ctx, { extension: opts.extension ?? 'avif' }));
}

/**
 * Build a `PageRef[]` for use with `ComicSource.getPages`. Each ref stashes
 * the image hash / avif-availability in `extra` so {@link buildPageImageUrl}
 * can later resolve without re-fetching the gallery.
 */
export async function buildPageRefs(id: string, signal?: AbortSignal): Promise<PageRef[]> {
  const raw = await fetchRawGallery(id, signal);
  const images = toHitomiImages(raw);
  return images.map((image, index) => ({
    comicId: String(raw.id ?? id),
    index,
    extra: {
      hash: image.hash,
      hasAvif: image.hasAvif,
    },
  }));
}

/**
 * Resolve a single PageRef to its final URL via cached gg.js.
 *
 * If `ref.extra` is missing (e.g. an upgraded reader-state shape) we
 * fall back to re-fetching the gallery — slower but correct.
 */
export async function resolvePageRefUrl(ref: PageRef): Promise<string> {
  const extra = ref.extra ?? {};
  const hash = typeof extra.hash === 'string' ? extra.hash : '';
  const hasAvif = Boolean(extra.hasAvif);
  if (hash) {
    const ctx = await getImageContext();
    return computeImageUrl({ hash, hasAvif }, ctx, { extension: hasAvif ? 'avif' : 'webp' });
  }
  const raw = await fetchRawGallery(ref.comicId);
  const urls = await getImageUrls(raw, { pageIdx: ref.index });
  const url = urls[0];
  if (!url) {
    throw new ParseError(`hitomi: no image at index ${ref.index} for ${ref.comicId}`, {
      source: 'hitomi',
    });
  }
  return url;
}

/**
 * Helper used by {@link searchByTag} / {@link listPopular} to turn a slice of
 * gallery IDs into a `Page<ComicCard>` by fetching each gallery's metadata.
 *
 * We fan out with `Promise.all` — hitomi's static origin handles light
 * concurrency well, and a 25-wide batch keeps the latency reasonable.
 */
async function materializeIdPage(
  ids: number[],
  page: number,
  pageSize: number,
  hasMoreFromNozomi: boolean,
  totalItems: number | undefined,
  signal?: AbortSignal,
): Promise<Page<ComicCard>> {
  const slice = ids.slice(0, pageSize);
  const items = await Promise.all(
    slice.map((id) => safeGalleryCard(String(id), signal)),
  );
  return {
    items: items.filter((x): x is ComicCard => x !== undefined),
    page,
    hasMore: hasMoreFromNozomi,
    ...(totalItems !== undefined ? { totalItems } : {}),
  };
}

async function safeGalleryCard(id: string, signal?: AbortSignal): Promise<ComicCard | undefined> {
  try {
    const raw = await fetchRawGallery(id, signal);
    return mapToComicCard(raw);
  } catch (err) {
    if (!isRecoverableGalleryMiss(err)) throw err;
    // Drop removed galleries without turning the whole page into an error.
    return undefined;
  }
}

function isRecoverableGalleryMiss(err: unknown): boolean {
  return err instanceof ParseError && /\bgallery \d+ not found \(404\)/.test(err.message);
}

/**
 * Parse a `"namespace:name"` (or bare `"name"`) tag string into a structured
 * `{namespace, name}` shape suitable for {@link searchByTag}. Bare names
 * default to the `tag` namespace.
 */
export function parseTagSpec(spec: string): { namespace: string; name: string } {
  const trimmed = spec.trim().toLowerCase();
  const colon = trimmed.indexOf(':');
  if (colon === -1) return { namespace: 'tag', name: trimmed };
  const namespace = trimmed.slice(0, colon);
  const name = trimmed.slice(colon + 1);
  if (!name) return { namespace: 'tag', name: trimmed };
  // Hitomi's tag namespace is sometimes spelled differently from ours; we
  // canonicalize to the namespaces it understands.
  const canonical = canonicalizeNamespace(namespace);
  return { namespace: canonical, name };
}

function canonicalizeNamespace(ns: string): string {
  switch (ns) {
    case 'parody':
      return 'series';
    default:
      return ns;
  }
}

/** Re-exported source id for callers building a `ComicCard` outside this module. */
export { HITOMI_SOURCE_ID };
/** Re-export so reader-only callers don't have to import from `./types`. */
export type { HitomiImage };
